import React, { useEffect, useMemo, useState } from 'react';

import ApproveAssessmentModal from '../../Assessments/Workspace/ApproveAssessmentModal';
import {
  getQcSummaryRows,
  groupQcIssuesByQuestion,
  OrderedQuestionEntry,
  QcSummaryRow,
} from '../../Assessments/Workspace/workspaceDerivations';
import { QcIssue } from '../../../interfaces/qcIssue.interface';
import { QCReviewItem } from '../../../interfaces/qcReviewData.interface';
import { approveWorkflowAssessment, getQCReviewData, getWorkflowQcIssues } from '../../../services/qcReview';

// The Approve Assessment dialog lives only behind the grid's Finalize action: confirming records the score, stamps the cycle, emails the assessor, then the caller runs the pre-existing finalization flow.
export default function FinalizeAssessmentModal(props: {
  open: boolean;
  workflowId: number | null;
  assessmentId?: number;
  surveyId?: number;
  companyName?: string;
  unitLabel?: string;
  assessorName?: string;
  proposedScore: number;
  onApproved: () => void;
  onClose: () => void;
}): JSX.Element | null {
  const {
    open, workflowId, assessmentId, surveyId, companyName, unitLabel, assessorName,
    proposedScore, onApproved, onClose,
  } = props;
  const [items, setItems] = useState<QCReviewItem[]>([]);
  const [issues, setIssues] = useState<QcIssue[]>([]);

  useEffect(() => {
    if (!open || !workflowId) return;
    const fetchSummary = async () => {
      try {
        const [reviewItems, workflowIssues] = await Promise.all([
          getQCReviewData(workflowId),
          getWorkflowQcIssues(workflowId),
        ]);
        setItems(reviewItems);
        setIssues(workflowIssues);
      } catch (error) {
        console.error('Failed to load QC summary for finalization', error);
      }
    };
    fetchSummary();
  }, [open, workflowId]);

  // Same synthesis as ContinueQcReviewModal — snapshot questions carry id/shortCode/title, all the dialog renders.
  const rows = useMemo<QcSummaryRow[]>(() => {
    const entries = items.map(item => (
      { question: { ...item.question, isBestPractice: false } } as unknown as OrderedQuestionEntry
    ));
    return getQcSummaryRows(entries, groupQcIssuesByQuestion(issues));
  }, [items, issues]);

  if (!workflowId) return null;

  const handleGoToQuestion = (row: QcSummaryRow) => {
    if (!assessmentId || !surveyId) return;
    window.open(
      `${window.location.origin}/assessment/${assessmentId}/questionnaire/${surveyId}?question=${row.entry.question.id}`,
      '_blank',
    );
  };

  return (
    <ApproveAssessmentModal
      open={open}
      companyName={companyName}
      unitLabel={unitLabel}
      assessorName={assessorName}
      rows={rows}
      proposedScore={proposedScore}
      onGoToQuestion={handleGoToQuestion}
      onApprove={async (finalScore, overrideReason, emailNotes) => {
        // Pessimistic: proceed only after the server stamps the cycle, records the score, and emails the assessor.
        try {
          await approveWorkflowAssessment(workflowId, {
            finalScore: Number(finalScore) || 0, overrideReason, emailNotes,
          });
          onApproved();
        } catch (error) {
          console.error('Failed to approve the QC review', error);
          throw error;
        }
      }}
      onClose={onClose}
    />
  );
}
